/**
 * @author Jorge Casas
 * @creationDate 17/07/2024
 */

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';

import { requestDownloadFileToConverBase64 } from '../../containers/assets/actions.js';
import { NotPicture } from '../pdfs/logoNoPicture.js';
import { generateHeader } from '../pdfs/Common.js';
import {
  formatDateLocalMexico,
  formatDateUTCWithTime,
} from '../../utils/additional-functions/format-date.js';
import { formatAmount } from '../../utils/additional-functions/format-amount.js';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * t() for the before/after values of the asset historics. The backend sends
 * the fields it does not normalize raw (assets_parser.js, default case):
 * booleans, objects and arrays, and i18next 10 throws on those in
 * extractFromKey. Strings and numbers go through t() as before, and
 * undefined, null and '' keep returning '' (what t() returns for them).
 */
const safeT = (t, value) => {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') return t(value);
  if (typeof value === 'boolean') return t(value ? 'common.yes' : 'common.no');
  if (typeof value === 'object') {
    const parts = (Array.isArray(value) ? value : Object.values(value)).filter(
      (part) => typeof part === 'string' || typeof part === 'number'
    );
    return parts.length > 0 ? parts.join(', ') : '—';
  }
  return '—';
};

/**
 * @function generateAssetDetailPDF
 *
 * @description Function to generate a detailed PDF for an asset
 *
 * @param {Object} assetData - The asset data
 * @returns {Promise} - The generated PDF
 * @throws {Promise} - The error message
 */
export const generateAssetDetailPDF = async (assetData, companyData, subcompanyData, props) => {
  console.log('🚀 ~ file: AssetDetail.js:32 ~ generateAssetDetailPDF ~ props:', props);
  console.log('Generating Asset Detail PDF for asset:', assetData);

  try {
    const header = await generateHeader({
      text: `${subcompanyData.name}`,
      alignment: 'center',
      style: 'textStyle',
      fontSize: 18,
      margin: [-20, 0, 0, 0],
    });

    const validImageTypes = ['jpg', 'jpeg', 'png'];

    const [imgEmployee, imgAsset, imgQRURL, imgBarcodeURL, ...imageResults] = await Promise.all([
      assetData._employee && assetData._employee.imgUrl !== undefined
        ? requestDownloadFileToConverBase64(assetData._employee.imgUrl).payload
        : Promise.resolve(NotPicture),

      assetData._files[0] && assetData._files[0].url !== undefined
        ? requestDownloadFileToConverBase64(assetData._files[0].url).payload
        : Promise.resolve(NotPicture),

      assetData.qrUrl
        ? requestDownloadFileToConverBase64(assetData.qrUrl).payload
        : Promise.resolve(NotPicture),

      assetData.barcodeUrl
        ? requestDownloadFileToConverBase64(assetData.barcodeUrl).payload
        : Promise.resolve(NotPicture),

      ...assetData._files
        .filter((file) => {
          const fileExtension = file.originalName.split('.').pop().toLowerCase();
          return validImageTypes.includes(fileExtension);
        })
        .map((file) => requestDownloadFileToConverBase64(file.url).payload),
    ]);

    let tableBody = [];
    let row = [];
    let imageCount = 0;

    for (let i = 0; i < imageResults.length; i++) {
      const imageUrl = imageResults[i];
      let fileExtension = 'png';

      if (i < assetData._files.length) {
        const file = assetData._files[i];
        if (file && file.originalName) {
          fileExtension = file.originalName.split('.').pop().toLowerCase();
        }
      }

      if (imageUrl !== NotPicture) {
        row.push({
          image: `data:image/${fileExtension};base64,${imageUrl}`,
          fit: [120, 120],
          alignment: 'center',
          margin: [5, 5, 5, 5],
        });
        imageCount++;

        if (row.length === 3) {
          tableBody.push(row);
          row = [];
        }
      }
    }

    if (row.length > 0) {
      while (row.length < 3) {
        row.push({ text: '', alignment: 'center' });
      }
      tableBody.push(row);
    }

    if (imageCount === 0) {
      tableBody.push([
        {
          text: 'No hay imágenes disponibles.',
          style: 'tableRow',
          alignment: 'center',
          fontSize: 8,
          colSpan: 3,
        },
        {},
        {},
      ]);
    }

    const docDefinition = {
      content: [
        header,
        {
          width: '*',
          text: `Fecha de Expedición Doc: ${moment().format('DD/MM/YYYY')}`,
          style: 'textStyle',
          alignment: 'right',
          fontSize: 8,
        },
        {
          margin: [0, 20, 0, 0],
          columnGap: 20,
          columns: [
            {
              width: '*',
              text: ``,
              bold: true,
              style: 'textStyle',
              alignment: 'center',
            },
            {
              width: '*',
              text: `Detalle del activo fijo\n\n${assetData.number ? assetData.number : ' - '} - ${
                assetData.name ? assetData.name : ' - '
              } - ${
                assetData.indexInteger[0] && assetData.indexInteger[0].numberString
                  ? assetData.indexInteger[0].numberString
                  : '- '
              }`,
              bold: true,
              style: 'textStyle',
              alignment: 'center',
            },
            {
              width: '*',
              text: ``,
              bold: true,
              style: 'textStyle',
              alignment: 'center',
            },
          ],
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            headerRows: 1,
            widths: [120, 10, '*'],
            body: [
              [
                {
                  image: `data:image/png;base64,${imgAsset}`,
                  width: 89,
                  height: 120,
                  alignment: 'center',
                  margin: [5, 5, 5, 5],
                },
                { text: ' ', border: [false, false, false, false] },
                {
                  stack: [
                    {
                      text: 'Fecha de compra:',
                      style: 'tableRow',
                      alignment: 'left',
                      margin: [0, 0, 0, 2],
                    },
                    {
                      text: `${
                        assetData.buyDate ? formatDateLocalMexico(assetData.buyDate) : ' - '
                      }`,
                      style: 'tableRow',
                      alignment: 'left',
                      bold: true,
                      margin: [20, 0, 0, 10],
                    },
                    {
                      text: 'Descripción del activo:',
                      style: 'tableRow',
                      alignment: 'left',
                      margin: [0, 0, 0, 2],
                    },
                    {
                      text: `${assetData.description ? assetData.description : ' - '}`,
                      style: 'tableRow',
                      alignment: 'left',
                      bold: true,
                      margin: [20, 0, 0, 10],
                    },
                    {
                      text: 'Fecha de alta en el sistema:',
                      style: 'tableRow',
                      alignment: 'left',
                      margin: [0, 0, 0, 2],
                    },
                    {
                      text: `${
                        assetData.createdAt ? formatDateUTCWithTime(assetData.createdAt) : ' - '
                      }`,
                      style: 'tableRow',
                      alignment: 'left',
                      bold: true,
                      margin: [20, 0, 0, 10],
                    },
                    {
                      text: 'Fecha de última modificación:',
                      style: 'tableRow',
                      alignment: 'left',
                      margin: [0, 0, 0, 2],
                    },
                    {
                      text: `${
                        assetData.updatedAt ? formatDateUTCWithTime(assetData.updatedAt) : ' - '
                      }`,
                      style: 'tableRow',
                      alignment: 'left',
                      bold: true,
                      margin: [20, 0, 0, 10],
                    },
                  ],
                  alignment: 'left',
                  border: [true, true, true, true],
                  fillColor: '#f9f9f9',
                  margin: [5, 5, 5, 5],
                  padding: [10, 10, 10, 10],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            header: {
              fontSize: 16,
              bold: true,
              margin: [0, 0, 0, 10],
            },
            tableRow: {
              fontSize: 12,
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Información general`,
          style: 'textStyle',
          alignment: 'left',
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            widths: [120, '*'],
            body: [
              [
                { text: 'Número', style: 'tableHeader', fontSize: 8 },
                {
                  text: assetData.number ? assetData.number : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Serie', style: 'tableHeader', fontSize: 8 },
                { text: assetData.serie ? assetData.serie : ' - ', style: 'tableRow', fontSize: 8 },
              ],
              [
                { text: 'Nombre', style: 'tableHeader', fontSize: 8 },
                { text: assetData.name ? assetData.name : ' - ', style: 'tableRow', fontSize: 8 },
              ],
              [
                { text: 'Modelo', style: 'tableHeader', fontSize: 8 },
                { text: assetData.mode ? assetData.model : ' - ', style: 'tableRow', fontSize: 8 },
              ],
              [
                { text: 'Marca', style: 'tableHeader', fontSize: 8 },
                { text: assetData.brand ? assetData.brand : ' - ', style: 'tableRow', fontSize: 8 },
              ],
              [
                { text: 'Estado Físico', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._physicalState && assetData._physicalState.value
                      ? props.t(assetData._physicalState.value)
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Fecha de Compra', style: 'tableHeader', fontSize: 8 },
                {
                  text: assetData.buyDate ? moment(assetData.buyDate).format('DD/MM/YYYY') : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],

              [
                { text: 'País', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._country && assetData._country.value
                      ? props.t(assetData._country.value)
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
            ].map((row, index) => {
              return index % 2 === 0 ? row.map((cell) => ({ ...cell, fillColor: '#f2f2f2' })) : row;
            }),
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'left',
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Ubicación`,
          style: 'textStyle',
          alignment: 'left',
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            widths: [120, '*'],
            body: [
              [
                { text: 'Responsable', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._employee && assetData._employee.name && assetData._employee.lastname
                      ? assetData._employee.name + ' ' + assetData._employee.lastname
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Ubicación', style: 'tableHeader', fontSize: 8 },
                {
                  text: assetData.location
                    ? (props.locations.find((location) => location.id === assetData.location) || {})
                        .name || ' - '
                    : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
            ].map((row, index) => {
              return index % 2 === 0 ? row.map((cell) => ({ ...cell, fillColor: '#f2f2f2' })) : row;
            }),
          },
          pageBreak: 'after',
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'left',
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Compra`,
          style: 'textStyle',
          alignment: 'left',
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            widths: [120, '*'],
            body: [
              [
                { text: 'Proveedor', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._provider && assetData._provider.name
                      ? assetData._provider.name
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Precio', style: 'tableHeader', fontSize: 8 },
                {
                  text: assetData.price ? '$ ' + formatAmount(assetData.price) : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Moneda', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._coin && assetData._coin.value
                      ? props.t(assetData._coin.value)
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Tipo de cambio', style: 'tableHeader', fontSize: 8 },
                {
                  text: assetData.exchange ? assetData.exchange : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Condición de compra', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._buyCondition && assetData._buyCondition.value
                      ? props.t(assetData._buyCondition.value)
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Centro de costos', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._costCenter && assetData._costCenter.name
                      ? assetData._costCenter.name
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Precio de compra en dolares ', style: 'tableHeader', fontSize: 8 },
                {
                  text: assetData.dollarAssetPrice
                    ? '$ ' + formatAmount(assetData.dollarAssetPrice)
                    : '$ - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
            ].map((row, index) => {
              return index % 2 === 0 ? row.map((cell) => ({ ...cell, fillColor: '#f2f2f2' })) : row;
            }),
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'left',
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Contable`,
          style: 'textStyle',
          alignment: 'left',
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            widths: [120, '*'],
            body: [
              [
                { text: 'Cuenta(Nombre)', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._account && assetData._account.key && assetData._account.name
                      ? assetData._account.key + ' - ' + assetData._account.name
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Tipo', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._subaccount && assetData._subaccount.name
                      ? assetData._subaccount.key + ' - ' + assetData._subaccount.name
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Fecha de aplicación', style: 'tableHeader', fontSize: 8 },
                {
                  text: `${
                    assetData.startDate ? formatDateLocalMexico(assetData.startDate) : ' - '
                  }`,
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Estatus', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._status && assetData._status.value
                      ? props.t(assetData._status.value)
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Depreciación financiera', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._financialDepreciation && assetData._financialDepreciation !== null
                      ? `${assetData._financialDepreciation.key} - ${assetData._financialDepreciation.name}`
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Depreciación fiscal', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._fiscalDepreciation && assetData._fiscalDepreciation !== null
                      ? `${assetData._fiscalDepreciation.key} - ${assetData._fiscalDepreciation.name}`
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Vida útil original', style: 'tableHeader', fontSize: 8 },
                {
                  text: assetData.usefulLifeOriginal ? assetData.usefulLifeOriginal : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
            ].map((row, index) => {
              return index % 2 === 0 ? row.map((cell) => ({ ...cell, fillColor: '#f2f2f2' })) : row;
            }),
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'left',
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Projecto`,
          style: 'textStyle',
          alignment: 'left',
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            widths: [120, '*'],
            body: [
              [
                { text: 'Projecto', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._project && assetData._project !== null
                      ? `${assetData._project.key} - ${assetData._project.name}`
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Costo de proyecto', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    '$ ' +
                    (assetData._project && assetData._project.amount !== undefined
                      ? formatAmount(assetData._project.amount)
                      : ' - '),
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Orden de compra', style: 'tableHeader', fontSize: 8 },
                {
                  text: assetData.buyOrder ? assetData.buyOrder : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
            ].map((row, index) => {
              return index % 2 === 0 ? row.map((cell) => ({ ...cell, fillColor: '#f2f2f2' })) : row;
            }),
          },
          pageBreak: 'after',
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'left',
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Seguro`,
          style: 'textStyle',
          alignment: 'left',
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            widths: [120, '*'],
            body: [
              [
                { text: 'Asegurable', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._insuranceCarrier && assetData._insuranceCarrier.active
                      ? assetData._insuranceCarrier.active === true
                        ? 'Si'
                        : 'No'
                      : '-',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],

              [
                { text: 'Aseguradora', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._insuranceCarrier && assetData._insuranceCarrier.value
                      ? assetData._insuranceCarrier.value
                      : '-',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Póliza de seguro', style: 'tableHeader', fontSize: 8 },
                {
                  text: assetData.insurancePolicy ? assetData.insurancePolicy : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Porcentaje asegurado', style: 'tableHeader', fontSize: 8 },
                {
                  text: assetData.insurancePercent ? assetData.insurancePercent + ' %' : ' - %',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
            ].map((row, index) => {
              return index % 2 === 0 ? row.map((cell) => ({ ...cell, fillColor: '#f2f2f2' })) : row;
            }),
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'left',
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Proveedor`,
          style: 'textStyle',
          alignment: 'left',
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            widths: [120, '*'],
            body: [
              [
                { text: 'Nombre', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._provider && assetData._provider.key && assetData._provider.name
                      ? assetData._provider.key + ' - ' + assetData._provider.name
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'RFC', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._provider && assetData._provider.rfc
                      ? assetData._provider.rfc
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Email', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._provider && assetData._provider.rfc
                      ? assetData._provider.mail
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Teléfono', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._provider && assetData._provider.telephone
                      ? assetData._provider.telephone
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
            ].map((row, index) => {
              return index % 2 === 0 ? row.map((cell) => ({ ...cell, fillColor: '#f2f2f2' })) : row;
            }),
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'left',
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Depreciación Financiera`,
          style: 'textStyle',
          alignment: 'left',
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            widths: [120, '*'],
            body: [
              [
                { text: 'M.O.I', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._financialDepreciationResult &&
                    assetData._financialDepreciationResult.originalAmount
                      ? '$ ' + formatAmount(assetData._financialDepreciationResult.originalAmount)
                      : '$ - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Depreciación acumulada', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._financialDepreciationResult &&
                    assetData._financialDepreciationResult.totalDepreciation
                      ? '$ ' +
                        formatAmount(assetData._financialDepreciationResult.totalDepreciation)
                      : ' $ -',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: '% Depreciación acumulada', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._financialDepreciationResult &&
                    assetData._financialDepreciationResult.totalPercentage
                      ? assetData._financialDepreciationResult.totalPercentage + ' %'
                      : ' - %',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Valor por depreciar', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._financialDepreciationResult &&
                    assetData._financialDepreciationResult.depreciable
                      ? '$ ' + formatAmount(assetData._financialDepreciationResult.depreciable)
                      : '$ -',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Vida remanente', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._financialDepreciationResult &&
                    assetData._financialDepreciationResult.lifeYears
                      ? assetData._financialDepreciationResult.lifeYears
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Depreciación aplicada', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._financialDepreciationResult &&
                    assetData._financialDepreciationResult.name
                      ? assetData._financialDepreciationResult.name
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
            ].map((row, index) => {
              return index % 2 === 0 ? row.map((cell) => ({ ...cell, fillColor: '#f2f2f2' })) : row;
            }),
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'left',
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Detalles de Depreciación Financiera Anual`,
          style: 'textStyle',
          alignment: 'center',
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Fecha de aplicación: ${
            assetData.startDate ? formatDateLocalMexico(assetData.startDate) : ' - '
          }\nFecha de valores: ${formatDateLocalMexico(new Date())}`,
          style: 'textStyle',
          alignment: 'left',
          fontSize: 8,
          bold: true,
        },
        {
          margin: [0, 10, 0, 0],
          table: {
            widths: [
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
            ],
            body: [
              [
                { text: 'Año', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Periodo', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                {
                  text: '#Meses dep. en el ejercicio',
                  style: 'tableHeader',
                  fontSize: 8,
                  alignment: 'center',
                },
                {
                  text: 'Total de meses a depreciarse',
                  style: 'tableHeader',
                  fontSize: 8,
                  alignment: 'center',
                },
                {
                  text: 'Total de meses dep. en ejercicios',
                  style: 'tableHeader',
                  fontSize: 8,
                  alignment: 'center',
                },
                { text: 'Factor', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Dep. Mensual', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Dep. Periodo', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: '% Dep. Periodo', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Dep. Acumulada', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                {
                  text: '% Dep. Acumulada',
                  style: 'tableHeader',
                  fontSize: 8,
                  alignment: 'center',
                },
                {
                  text: 'Valor por depreciar',
                  style: 'tableHeader',
                  fontSize: 8,
                  alignment: 'center',
                },
              ],
              ...(assetData._financialDepreciationResult &&
              assetData._financialDepreciationResult.yearly
                ? Object.keys(assetData._financialDepreciationResult.yearly).map(
                    (year, index, years) => {
                      const monthlyData =
                        assetData._financialDepreciationResult.yearly[year].monthly;
                      const firstMonth = Object.keys(monthlyData)[0];
                      const lastMonth = Object.keys(monthlyData).slice(-1)[0];

                      const numMonths = Object.keys(monthlyData).length;

                      let totalMonths = numMonths;
                      if (index > 0) {
                        const previousYear = years[index - 1];
                        totalMonths += Object.keys(
                          assetData._financialDepreciationResult.yearly[previousYear].monthly
                        ).length;
                      }

                      return [
                        { text: year, style: 'tableRow', fontSize: 8 },
                        {
                          text: `${props.t(`months.${firstMonth}`)} - ${props.t(
                            `months.${lastMonth}`
                          )}`,
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        { text: numMonths, style: 'tableRow', fontSize: 8 },
                        { text: totalMonths, style: 'tableRow', fontSize: 8 },
                        {
                          text: assetData._financialDepreciationResult.totalLifeMonths,
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text: assetData._financialDepreciationResult.factors.factor,
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text: assetData._financialDepreciationResult.depMonthly,
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text: assetData._financialDepreciationResult.yearly[year].depreciation,
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text: assetData._financialDepreciationResult.yearly[year]
                            .depreciationPercentage,
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text: assetData._financialDepreciationResult.yearly[year]
                            .acummDepreciation,
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text: assetData._financialDepreciationResult.yearly[year]
                            .acummDepreciationPercentage,
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text: assetData._financialDepreciationResult.yearly[year].depreciable,
                          style: 'tableRow',
                          fontSize: 8,
                        },
                      ];
                    }
                  )
                : []),
            ],
          },
          pageBreak: 'after',
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'left',
              margin: [0, 5, 0, 5],
            },
            textStyle: {
              fontSize: 10,
              bold: true,
              margin: [0, 0, 0, 10],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Depreciación Fiscal`,
          style: 'textStyle',
          alignment: 'left',
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            widths: [120, '*'],
            body: [
              [
                { text: 'M.O.I', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._fiscalDepreciationResult &&
                    assetData._fiscalDepreciationResult.originalAmount
                      ? '$ ' + formatAmount(assetData._fiscalDepreciationResult.originalAmount)
                      : ' $ - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Depreciación acumulada', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._fiscalDepreciationResult &&
                    assetData._fiscalDepreciationResult.totalDepreciation
                      ? '$ ' + formatAmount(assetData._fiscalDepreciationResult.totalDepreciation)
                      : '$ - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: '% Depreciación acumulada', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._fiscalDepreciationResult &&
                    assetData._fiscalDepreciationResult.totalPercentage
                      ? assetData._fiscalDepreciationResult.totalPercentage + ' %'
                      : ' - %',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Valor por depreciar', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._fiscalDepreciationResult &&
                    assetData._fiscalDepreciationResult.depreciable
                      ? '$ ' + formatAmount(assetData._fiscalDepreciationResult.depreciable)
                      : '$ - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Vida remanente', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._fiscalDepreciationResult &&
                    assetData._fiscalDepreciationResult.lifeYears
                      ? assetData._fiscalDepreciationResult.lifeYears
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
              [
                { text: 'Depreciación aplicada', style: 'tableHeader', fontSize: 8 },
                {
                  text:
                    assetData._fiscalDepreciationResult && assetData._fiscalDepreciationResult.name
                      ? assetData._fiscalDepreciationResult.name
                      : ' - ',
                  style: 'tableRow',
                  fontSize: 8,
                },
              ],
            ].map((row, index) => {
              return index % 2 === 0 ? row.map((cell) => ({ ...cell, fillColor: '#f2f2f2' })) : row;
            }),
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'left',
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Detalles de Depreciación Fiscal Anual`,
          style: 'textStyle',
          alignment: 'center',
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Fecha de aplicación: ${
            assetData.startDate ? formatDateLocalMexico(assetData.startDate) : ' - '
          }\nFecha de valores: ${formatDateLocalMexico(new Date())}`,
          style: 'textStyle',
          alignment: 'left',
          fontSize: 8,
          bold: true,
        },
        {
          margin: [0, 10, 0, 0],
          table: {
            widths: [
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
              'auto',
            ],
            body: [
              [
                { text: 'Año', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Periodo', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                {
                  text: '#Meses dep. en el ejercicio',
                  style: 'tableHeader',
                  fontSize: 8,
                  alignment: 'center',
                },
                {
                  text: 'Total de meses a depreciarse',
                  style: 'tableHeader',
                  fontSize: 8,
                  alignment: 'center',
                },
                {
                  text: 'Total de meses dep. en ejercicios',
                  style: 'tableHeader',
                  fontSize: 8,
                  alignment: 'center',
                },
                { text: 'Factor', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Dep. Mensual', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Dep. Periodo', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: '% Dep. Periodo', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Dep. Acumulada', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                {
                  text: '% Dep. Acumulada',
                  style: 'tableHeader',
                  fontSize: 8,
                  alignment: 'center',
                },
                {
                  text: 'Valor por depreciar',
                  style: 'tableHeader',
                  fontSize: 8,
                  alignment: 'center',
                },
              ],
              ...(assetData._fiscalDepreciationResult && assetData._fiscalDepreciationResult.yearly
                ? Object.keys(assetData._fiscalDepreciationResult.yearly).map(
                    (year, index, years) => {
                      const monthlyData = assetData._fiscalDepreciationResult.yearly[year].monthly;
                      const firstMonth = Object.keys(monthlyData)[0];
                      const lastMonth = Object.keys(monthlyData).slice(-1)[0];

                      const numMonths = Object.keys(monthlyData).length;

                      let totalMonths = numMonths;
                      if (index > 0) {
                        const previousYear = years[index - 1];
                        totalMonths += Object.keys(
                          assetData._fiscalDepreciationResult.yearly[previousYear].monthly
                        ).length;
                      }

                      return [
                        { text: year, style: 'tableRow', fontSize: 8 },
                        {
                          text: `${props.t(`months.${firstMonth}`)} - ${props.t(
                            `months.${lastMonth}`
                          )}`,
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        { text: numMonths, style: 'tableRow', fontSize: 8 },
                        { text: totalMonths, style: 'tableRow', fontSize: 8 },
                        {
                          text: assetData._fiscalDepreciationResult.totalLifeMonths,
                          style: 'tableRow',
                        },
                        {
                          text: assetData._fiscalDepreciationResult.factors.factor || '-',
                          style: 'tableRow',
                        },
                        {
                          text: assetData._fiscalDepreciationResult.depMonthly || '-',
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text:
                            assetData._fiscalDepreciationResult.yearly[year].depreciation || '-',
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text:
                            assetData._fiscalDepreciationResult.yearly[year]
                              .depreciationPercentage || '-',
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text:
                            assetData._fiscalDepreciationResult.yearly[year].acummDepreciation ||
                            '-',
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text:
                            assetData._fiscalDepreciationResult.yearly[year]
                              .acummDepreciationPercentage || '-',
                          style: 'tableRow',
                          fontSize: 8,
                        },
                        {
                          text: assetData._fiscalDepreciationResult.yearly[year].depreciable || '-',
                          style: 'tableRow',
                          fontSize: 8,
                        },
                      ];
                    }
                  )
                : []),
            ],
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'left',
              margin: [0, 5, 0, 5],
            },
            textStyle: {
              fontSize: 10,
              bold: true,
              margin: [0, 0, 0, 10],
            },
          },
        },
        //Aqui
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Información de Asignación`,
          style: 'textStyle',
          alignment: 'center',
          bold: true,
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            headerRows: 1,
            widths: [120, '*'],
            body: [
              [
                {
                  image: `data:image/png;base64,${imgEmployee}`,
                  width: 89,
                  height: 120,
                  alignment: 'center',
                  margin: [5, 5, 5, 5],
                },
                {
                  stack: [
                    {
                      text: 'Información del Empleado',
                      style: 'header',
                      alignment: 'left',
                      margin: [0, 0, 0, 10],
                    },
                    {
                      text: `Número de empleado: ${
                        assetData._employee && assetData._employee.key
                          ? assetData._employee.key
                          : ' - '
                      }`,
                      style: 'tableRow',
                    },
                    {
                      text: `Nombre: ${
                        assetData._employee && assetData._employee.name
                          ? assetData._employee.name
                          : ' - '
                      } ${
                        assetData._employee && assetData._employee.lastname
                          ? assetData._employee.lastname
                          : ' - '
                      }`,
                      style: 'tableRow',
                    },
                    {
                      text: `Puesto: ${
                        assetData._employee && assetData._employee.position
                          ? assetData._employee.position
                          : ' - '
                      }`,
                      style: 'tableRow',
                    },
                    {
                      text: `Email: ${
                        assetData._employee && assetData._employee.email
                          ? assetData._employee.email
                          : ' - '
                      }`,
                      style: 'tableRow',
                    },
                  ],
                  alignment: 'left',
                  border: [true, true, true, true],
                  fillColor: '#f9f9f9',
                  margin: [5, 5, 5, 5],
                  padding: [10, 10, 10, 10],
                },
              ],
            ],
          },
          pageBreak: 'after',
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            header: {
              fontSize: 16,
              bold: true,
              margin: [0, 0, 0, 10],
            },
            tableRow: {
              fontSize: 12,
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Historial de Cambios del Activo`,
          style: 'textStyle',
          alignment: 'center',
          bold: true,
        },
        {
          margin: [0, 10, 0, 0],
          table: {
            widths: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Fecha', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Usuario', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Eatus', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Campo', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Valor Anterior', style: 'tableHeader', fontSize: 8, alignment: 'center' },
                { text: 'Valor Nuevo', style: 'tableHeader', fontSize: 8, alignment: 'center' },
              ],
              ...(props.historics && Object.keys(props.historics).length > 0
                ? Object.keys(props.historics)
                    .map((key) => {
                      const record = props.historics[key];

                      return [
                        {
                          text: record.createdAt ? formatDateUTCWithTime(record.createdAt) : ' - ',
                          style: 'tableRow',
                          fontSize: 8,
                          alignment: 'center',
                        },
                        {
                          text: `${
                            record._createdBy && record._createdBy.name
                              ? record._createdBy.name
                              : ' - '
                          }
                              ${
                                record._createdBy && record._createdBy.lastname
                                  ? record._createdBy.lastname
                                  : ' - '
                              }`,
                          style: 'tableRow',
                          fontSize: 8,
                          alignment: 'center',
                        },
                        {
                          text: `${
                            record.status
                              ? props.t(`assets.fields.historics.statusName.${record.status}`)
                              : ' - '
                          }`,
                          style: 'tableRow',
                          fontSize: 8,
                          alignment: 'center',
                        },
                        {
                          text: `${record.differences
                            .map((difference) => props.t(`assets.fields.${difference.field}`))
                            .join('\n\n ')}`,
                          style: 'tableRow',
                          fontSize: 8,
                          alignment: 'center',
                        },
                        {
                          text: `${record.differences
                            .map((difference) => safeT(props.t, difference.before))
                            .join('\n\n ')}`,
                          style: 'tableRow',
                          fontSize: 8,
                          alignment: 'center',
                        },
                        {
                          text: `${record.differences
                            .map((difference) => safeT(props.t, difference.after))
                            .join('\n\n ')}`,
                          style: 'tableRow',
                          fontSize: 8,
                          alignment: 'center',
                        },
                      ];
                    })
                    .slice(0, 10)
                : [
                    [
                      {
                        text: 'No hay datos disponibles',
                        colSpan: 6,
                        alignment: 'center',
                        fontSize: 8,
                        style: 'tableRow',
                      },
                    ],
                  ]),
            ],
          },
          pageBreak: 'after',
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            tableHeader: {
              fontSize: 8,
              bold: true,
              fillColor: '#f8f9fa',
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
            tableRow: {
              fontSize: 8,
              alignment: 'center',
              margin: [0, 5, 0, 5],
            },
          },
          absolutePosition:
            props.historics && Object.keys(props.historics).length === 0
              ? { x: 180, y: 100 }
              : { x: 20, y: 100 },
          width: props.historics && Object.keys(props.historics).length === 0 ? 520 : undefined,
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Códigos del Activo`,
          style: 'textStyle',
          alignment: 'center',
          bold: true,
        },
        {
          margin: [0, 20, 0, 0],
          columns: [
            {
              stack: [
                {
                  image: `data:image/png;base64,${imgQRURL}`,
                  width: 100,
                  height: 100,
                  alignment: 'center',
                  margin: [50, 5, 5, 5],
                },
                {
                  text: 'Código QR',
                  alignment: 'center',
                  style: 'textStyle',
                  fontSize: 8,
                  margin: [0, 5, 0, 0],
                },
              ],
              alignment: 'center',
            },
            {
              stack: [
                {
                  image: `data:image/png;base64,${imgBarcodeURL}`,
                  width: 200,
                  height: 30,
                  alignment: 'left',
                  margin: [5, 40, -25, 5],
                },
                {
                  text: 'Código de barras',
                  alignment: 'center',
                  style: 'textStyle',
                  fontSize: 8,
                  margin: [0, 40, 0, 0],
                },
              ],
              alignment: 'center',
            },
          ],
          columnGap: 100,
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Reporte fotográfico`,
          style: 'textStyle',
          alignment: 'center',
          bold: true,
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            headerRows: 1,
            widths: ['33.33%', '33.33%', '33.33%'],
            body: tableBody,
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 ? 2 : 1;
            },
            vLineWidth: function (i, node) {
              return 0;
            },
            hLineColor: function (i, node) {
              return '#e9ecef';
            },
            vLineColor: function (i, node) {
              return '#e9ecef';
            },
          },
          styles: {
            header: {
              fontSize: 16,
              bold: true,
              margin: [0, 0, 0, 10],
            },
            tableRow: {
              fontSize: 12,
              margin: [0, 5, 0, 5],
            },
          },
        },
        {
          margin: [0, 20, 0, 0],
          width: '*',
          text: `Listado de Archivos Adjuntos`,
          style: 'textStyle',
          alignment: 'center',
          bold: true,
        },
        {
          margin: [0, 20, 0, 0],
          text:
            assetData &&
            assetData._files &&
            Array.isArray(assetData._files) &&
            assetData._files.length > 0
              ? assetData._files.map((file) => file.originalName).join('\n\n')
              : ' No hay archivos adjuntos.',
          style: 'tableRow',
          fontSize: 8,
          alignment: 'left',
        },
      ],
      footer: function (currentPage, pageCount) {
        return {
          margin: [30, -30, 30, 0],
          table: {
            widths: ['*', '*', '*'],
            body: [
              [
                {
                  text: `Fecha de Emisión:\n${formatDateUTCWithTime(new Date())}`,
                  style: 'footerText',
                  alignment: 'center',
                  alignment: 'center',
                  border: [false, true, false, true],
                },
                {
                  text: 'Versión: 1.0',
                  style: 'footerText',
                  alignment: 'center',
                  border: [false, true, false, true],
                },
                {
                  text: `Página ${currentPage} de ${pageCount}`,
                  style: 'footerText',
                  alignment: 'center',
                  border: [false, true, false, true],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 || i === 2 ? 1 : 0;
            },
            vLineWidth: function (i, node) {
              return 1;
            },
            hLineColor: function (i, node) {
              return '#ccc';
            },
            vLineColor: function (i, node) {
              return '#ccc';
            },
          },
          styles: {
            footerText: {
              color: '#45535b',
              fontSize: 7,
              margin: [5, 2, 5, 2],
            },
          },
        };
      },
      styles: {
        textStyle: {
          color: '#45535b',
          fontSize: 11,
          margin: [0, 20, 0, 0],
        },
        footerText: {
          color: '#45535b',
          fontSize: 6.5,
          margin: [5, 2, 5, 2],
        },
        tableRow: {
          color: '#45535b',
          fontSize: 9,
          margin: [10, 5, 0, 5],
        },
      },
    };

    pdfMake.createPdf(docDefinition).open();
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw { code: 500, message: 'Error generating PDF' };
  }
};



// WEBPACK FOOTER //
// src/lib/pdfs/AssetDetail.js